import { useRef, useState, type DragEvent, type ChangeEvent, type KeyboardEvent } from 'react';
import { API_BASE } from '../lib/api';
import BundleMappingApproval from './BundleMappingApproval';

// Sprint 11 UX.F1 — Drag-drop ZIP uploader for Claude Design handoff
// bundles. Calls POST /projects/:projectId/claude-design/upload, surfaces
// the parsed summary + mapping plan inline. No chat integration in this
// PR — the agent dialogue's "Use Claude Design bundle" flow is a follow-
// up story (UX agent prompt addition only), and renders this component.

const MAX_BUNDLE_BYTES = 50 * 1024 * 1024; // mirrors apps/api/src/routes/claude-design.ts

interface ParsedSummary {
  parsed: {
    metadata: {
      bundleVersion:   string;
      framework:       string;
      fileCount:       number;
      totalSize:       number;
      hasComponents:   boolean;
      hasDesignSystem: boolean;
      degradedReason?: string;
      warnings:        string[];
    };
    components: { name: string; estimatedTier: string }[];
    screens:    { name: string; route?: string }[];
    designTokens: { source: string; colors: Record<string, string> } | null;
  };
  mappingPlan: {
    newScreens:        { id: string; name: string; route?: string }[];
    newComponents:     { id: string; name: string; type?: string }[];
    componentConflicts: { componentName: string; field: string; bundleValue: string; existingValue: string }[];
    matchedScreens:    string[];
    matchedComponents: string[];
  };
  artefactId: string;
}

interface Props {
  projectId: string;
  onUploaded?: (response: ParsedSummary) => void;
}

export default function ClaudeDesignBundleUpload({ projectId, onUploaded }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging]   = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [summary, setSummary]     = useState<ParsedSummary | null>(null);

  const upload = async (file: File) => {
    setError(null);
    setSummary(null);
    if (file.size > MAX_BUNDLE_BYTES) {
      setError(`Bundle is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 50 MB.`);
      return;
    }
    if (!file.name.toLowerCase().endsWith('.zip')) {
      setError(`Expected a ZIP file. Got "${file.name}".`);
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('bundle', file);
      const token = localStorage.getItem('blueprint_token');
      const res = await fetch(`${API_BASE}/projects/${projectId}/claude-design/upload`, {
        method:  'POST',
        body:    form,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error || `Upload failed: HTTP ${res.status}`);
      }
      const json = (await res.json()) as ParsedSummary;
      setSummary(json);
      onUploaded?.(json);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setUploading(false);
    }
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void upload(file);
  };
  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) void upload(file);
  };
  const onDragOver  = (e: DragEvent<HTMLDivElement>) => { e.preventDefault(); };
  const onDragEnter = (e: DragEvent<HTMLDivElement>) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = (e: DragEvent<HTMLDivElement>) => { e.preventDefault(); setDragging(false); };

  return (
    <div className="space-y-4">
      <div
        role="button"
        tabIndex={0}
        aria-disabled={uploading}
        onClick={() => !uploading && inputRef.current?.click()}
        onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
          if (uploading) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={onDragOver}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        style={{
          border:        `2px dashed ${dragging ? 'var(--primary)' : 'var(--border)'}`,
          borderRadius:  'var(--radius-md)',
          padding:       '32px',
          textAlign:     'center',
          cursor:        uploading ? 'progress' : 'pointer',
          background:    dragging ? 'var(--surface-elevated)' : 'var(--surface)',
          transition:    'background 150ms, border-color 150ms',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".zip,application/zip"
          style={{ display: 'none' }}
          onChange={onChange}
          disabled={uploading}
        />
        <p style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--text)', margin: 0 }}>
          {uploading ? 'Uploading and parsing…' : 'Drop a Claude Design bundle (ZIP) here, or click to choose a file'}
        </p>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: '8px 0 0' }}>
          Up to 50 MB. README.md and PROMPT.md required at the bundle root.
        </p>
      </div>

      {error && (
        <div role="alert" style={{ padding: 12, borderRadius: 'var(--radius-sm)', background: 'var(--error-bg)', color: 'var(--error)', fontFamily: 'var(--font-body)', fontSize: 13 }}>
          {error}
        </div>
      )}

      {summary && (
        <div style={{ padding: 16, borderRadius: 'var(--radius-md)', background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--text)', margin: '0 0 8px' }}>
            Bundle parsed
          </h3>
          <ul style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', margin: 0, paddingLeft: 18 }}>
            <li>Bundle version: <code>{summary.parsed.metadata.bundleVersion}</code></li>
            <li>Framework: <code>{summary.parsed.metadata.framework}</code></li>
            <li>{summary.parsed.components.length} component file{summary.parsed.components.length === 1 ? '' : 's'}</li>
            <li>{summary.parsed.screens.length} inferred screen{summary.parsed.screens.length === 1 ? '' : 's'}</li>
            {summary.parsed.designTokens && (
              <li>Design tokens from <code>{summary.parsed.designTokens.source}</code> ({Object.keys(summary.parsed.designTokens.colors).length} colours)</li>
            )}
          </ul>
          <h4 style={{ fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600, color: 'var(--text)', margin: '12px 0 6px' }}>
            Mapping plan
          </h4>
          <ul style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', margin: 0, paddingLeft: 18 }}>
            <li>New screens: <strong>{summary.mappingPlan.newScreens.length}</strong></li>
            <li>New components: <strong>{summary.mappingPlan.newComponents.length}</strong></li>
            <li>Component conflicts: <strong style={{ color: summary.mappingPlan.componentConflicts.length > 0 ? 'var(--warning)' : 'inherit' }}>{summary.mappingPlan.componentConflicts.length}</strong></li>
            <li>Already-present screens: {summary.mappingPlan.matchedScreens.length}</li>
            <li>Already-present components: {summary.mappingPlan.matchedComponents.length}</li>
          </ul>
          {summary.parsed.metadata.warnings.length > 0 && (
            <>
              <h4 style={{ fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600, color: 'var(--warning)', margin: '12px 0 6px' }}>
                Warnings
              </h4>
              <ul style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: 0, paddingLeft: 18 }}>
                {summary.parsed.metadata.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </>
          )}
        </div>
      )}

      {/* Sprint 11 UX.F2 (F1 follow-up) — Approval flow renders inline once
          the upload succeeds. The PM can deselect items they don't want
          merged before clicking "Approve & Merge". */}
      {summary && (
        <BundleMappingApproval
          projectId={projectId}
          bundleArtefactId={summary.artefactId}
          plan={summary.mappingPlan}
        />
      )}
    </div>
  );
}
